/**
 * Ownership DTO → View mapper (SCRUM-172).
 *
 * Pure BE → FE projection for the Property detail Owner tab. Mirrors
 * the posture of `dto-mapper.ts`:
 *
 *   1. **Field-name alignment.** `share` → `sharePercent`, `userId` →
 *      a resolved `ownerName` (Decision 1: resolve via the org-users
 *      lookup, fall back to the raw `userId`).
 *   2. **Derived state.** `isActive` is computed from the absence of
 *      `effectiveTo` — the current owner is the record with no end
 *      date. Pages consume the boolean instead of re-deriving it.
 *   3. **Nullable normalisation.** The BE marks `contractDocument` /
 *      `contractName` / `contractReference` / `effectiveTo` /
 *      `endedReason` / `expiresAt` nullable; the mapper drops `null`
 *      to keep `OwnershipView` optionals `undefined` (never coerce to
 *      mock data — same rule as `types.ts`).
 *
 * `share` is a percentage `number` (0–100), NOT `Cents` — there is no
 * money arithmetic here. Dates are passed through as ISO date-time
 * strings and formatted at the render boundary.
 *
 * Unit coverage in `__tests__/ownership-mapper.spec.ts` — name
 * resolution + fallback, isActive both branches, every nullable
 * dropped vs. present.
 */

import type { components } from '@artemis/contracts';
import type { ContractDocumentView, OwnershipView } from './types.js';

type OwnershipResponseDto = components['schemas']['OwnershipResponseDto'];

/**
 * A read-only `userId → displayName` resolver. The Owner tab passes a
 * `Map` built from the org-users query (`useOrgUsers`); the mapper
 * stays pure by taking the already-resolved name (or `undefined`)
 * rather than fetching.
 */
export type OwnerNameResolver = (userId: string) => string | undefined;

function projectContractDocument(
  doc: OwnershipResponseDto['contractDocument'],
): ContractDocumentView | undefined {
  if (doc === null || doc === undefined) return undefined;
  return {
    filename: doc.filename,
    objectKey: doc.objectKey,
    sizeBytes: doc.sizeBytes,
    uploadedAt: doc.uploadedAt,
  };
}

/**
 * Project a single `OwnershipResponseDto` to `OwnershipView`.
 * Pure — no fetches, no defaults beyond the explicit owner-name
 * fallback. `resolveOwnerName` is optional; when omitted (or it
 * returns `undefined`) the view's `ownerName` falls back to the raw
 * `userId` so the card always renders *something* honest.
 */
export function projectOwnershipDto(
  dto: OwnershipResponseDto,
  resolveOwnerName?: OwnerNameResolver,
): OwnershipView {
  const resolved = resolveOwnerName?.(dto.userId);
  const contractDocument = projectContractDocument(dto.contractDocument);
  const view: OwnershipView = {
    id: dto.id,
    propertyId: dto.propertyId,
    userId: dto.userId,
    ownerName:
      resolved !== undefined && resolved.length > 0 ? resolved : dto.userId,
    sharePercent: dto.share,
    effectiveFrom: dto.effectiveFrom,
    isActive: dto.effectiveTo === null || dto.effectiveTo === undefined,
    version: dto.version,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    ...(dto.effectiveTo !== null && dto.effectiveTo !== undefined
      ? { effectiveTo: dto.effectiveTo }
      : {}),
    ...(dto.expiresAt !== null && dto.expiresAt !== undefined
      ? { expiresAt: dto.expiresAt }
      : {}),
    ...(dto.endedReason !== null && dto.endedReason !== undefined
      ? { endedReason: dto.endedReason }
      : {}),
    ...(dto.contractName !== null && dto.contractName !== undefined
      ? { contractName: dto.contractName }
      : {}),
    ...(dto.contractReference !== null && dto.contractReference !== undefined
      ? { contractReference: dto.contractReference }
      : {}),
    ...(contractDocument !== undefined ? { contractDocument } : {}),
  };
  return view;
}
